import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  FilePlus, 
  Files, 
  Users, 
  Buildings, 
  CurrencyDollar,
  TrendUp,
  ArrowRight,
  CalendarBlank,
  Bed,
  Compass,
  Stack,
  CaretDown,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get("/dashboard/stats");
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "status-confirmed";
      case "sent": return "status-sent";
      case "cancelled": return "status-cancelled";
      default: return "status-draft";
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Dashboard</p>
          <h1 className="font-serif text-4xl text-navy">Welcome Back</h1>
        </div>
        <div className="relative" ref={menuRef}>
          <Button
            onClick={() => setShowNewMenu((v) => !v)}
            className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
            data-testid="create-proposal-btn"
          >
            <FilePlus size={18} />
            New Proposal
            <CaretDown size={12} />
          </Button>
          {showNewMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-border shadow-lg z-50 w-56">
              {[
                { icon: Bed, label: "Hotel Proposal", type: "hotels" },
                { icon: Compass, label: "Sightseeing Proposal", type: "sightseeing" },
                { icon: Stack, label: "Combined Proposal", type: "combined" },
              ].map((pt) => (
                <button
                  key={pt.type}
                  onClick={() => { navigate(`/proposals/new?type=${pt.type}`); setShowNewMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-cream transition-colors"
                  data-testid={`dashboard-new-${pt.type}`}
                >
                  <pt.icon size={16} className="text-gold" />
                  <span>{pt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="bg-white border border-border p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              This Month
            </span>
            <div className="w-10 h-10 bg-navy/5 flex items-center justify-center">
              <Files size={20} className="text-navy" />
            </div>
          </div>
          <p className="font-serif text-3xl text-navy">{stats?.proposals_this_month || 0}</p>
          <p className="text-sm text-muted-foreground mt-1">Proposals created</p>
        </div>

        <div className="bg-white border border-border p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Total Revenue
            </span>
            <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
              <CurrencyDollar size={20} className="text-gold" />
            </div>
          </div>
          <p className="font-serif text-3xl text-navy">{formatCurrency(stats?.total_revenue)}</p>
          <p className="text-sm text-muted-foreground mt-1">All time</p>
        </div>

        <div className="bg-white border border-border p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Total Margin
            </span>
            <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center">
              <TrendUp size={20} className="text-emerald-600" />
            </div>
          </div>
          <p className="font-serif text-3xl text-navy">{formatCurrency(stats?.total_margin)}</p>
          <p className="text-sm text-muted-foreground mt-1">Gross profit</p>
        </div>

        <div className="bg-white border border-border p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Resources
            </span>
            <div className="w-10 h-10 bg-navy/5 flex items-center justify-center">
              <Users size={20} className="text-navy" />
            </div>
          </div>
          <div className="flex items-baseline gap-4">
            <div>
              <p className="font-serif text-3xl text-navy">{stats?.total_clients || 0}</p>
              <p className="text-sm text-muted-foreground">Clients</p>
            </div>
            <div className="border-l border-border pl-4">
              <p className="font-serif text-3xl text-navy">{stats?.total_hotels || 0}</p>
              <p className="text-sm text-muted-foreground">Hotels</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Proposals */}
      <div className="bg-white border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-serif text-xl text-navy">Recent Proposals</h2>
          <Button
            variant="ghost"
            className="text-gold hover:text-gold/80 hover:bg-gold/5 gap-1"
            onClick={() => navigate("/proposals")}
            data-testid="view-all-proposals"
          >
            View all <ArrowRight size={16} />
          </Button>
        </div>

        {stats?.recent_proposals?.length > 0 ? (
          <div className="divide-y divide-border">
            {stats.recent_proposals.map((proposal) => (
              <div
                key={proposal.id}
                className="p-6 flex items-center justify-between hover:bg-cream/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/proposals/${proposal.id}/edit`)}
                data-testid={`proposal-row-${proposal.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-mono text-muted-foreground">{proposal.reference}</span>
                    <span className={`text-xs px-2 py-0.5 ${getStatusClass(proposal.status)}`}>
                      {proposal.status}
                    </span>
                  </div>
                  <p className="font-medium text-navy truncate">{proposal.client_name}</p>
                  <p className="text-sm text-muted-foreground truncate">{proposal.trip_title || "Untitled Trip"}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="font-serif text-lg text-gold">
                    {formatCurrency(proposal.total_value)}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center justify-end gap-1 mt-1">
                    <CalendarBlank size={12} />
                    {proposal.created_at ? format(new Date(proposal.created_at), "MMM d, yyyy") : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Files size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">No proposals yet</p>
            <Button
              onClick={() => navigate("/proposals/new")}
              className="bg-gold hover:bg-gold/90 text-white"
            >
              Create your first proposal
            </Button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <button
          onClick={() => navigate("/proposals/new")}
          className="bg-white border border-border p-6 text-left hover:border-navy/30 transition-colors group"
          data-testid="quick-new-proposal"
        >
          <FilePlus size={24} className="text-navy mb-3" />
          <h3 className="font-medium text-navy mb-1">New Proposal</h3>
          <p className="text-sm text-muted-foreground">Create a multi-city luxury proposal</p>
        </button>

        <button
          onClick={() => navigate("/clients")}
          className="bg-white border border-border p-6 text-left hover:border-navy/30 transition-colors group"
          data-testid="quick-clients"
        >
          <Users size={24} className="text-navy mb-3" />
          <h3 className="font-medium text-navy mb-1">Client Profiles</h3>
          <p className="text-sm text-muted-foreground">View and manage your client base</p>
        </button>

        <button
          onClick={() => navigate("/hotels")}
          className="bg-white border border-border p-6 text-left hover:border-navy/30 transition-colors group"
          data-testid="quick-hotels"
        >
          <Buildings size={24} className="text-navy mb-3" />
          <h3 className="font-medium text-navy mb-1">Hotel Library</h3>
          <p className="text-sm text-muted-foreground">Access your saved properties</p>
        </button>
      </div>
    </div>
  );
};

export default DashboardPage;
